import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BuslogPreparationStepper from '../common/BuslogPreparationStepper/BuslogPreparationStepper';
import OverlayChart from './OverlayChart';
import MapPanel from './MapPanel';
import TablePanel from './TablePanel';
import PlaybackControls from './PlaybackControls';

const BuslogPanels = ({
  prep,
  signals,
  playback,
  playbackTimestamp,
  filePath,
  isSaved,
  handleSaveSearchClick,
  currentSignalsParam
}) => {
  useEffect(() => {
    if (prep.isFileReady) {
      signals.loadGpsSignals();
    }
  }, [prep.isFileReady, signals.loadGpsSignals]);

  const hasPlayback = playback.durationSec > 0;

  return (
    <Box className="visualize-content">
      {prep.isEmpty ? (
        <Box className="charts-empty-state">
          <InsertDriveFileIcon className="visualize-empty-state-file-icon" />
          <Typography variant="h6" color="text.secondary" gutterBottom>No Signals Found</Typography>
          <Typography variant="body2" color="text.secondary" className="visualize-empty-state-text">
            {prep.currentMessage || 'This bus log file contains no decodable signals. It may be empty or corrupt.'}
          </Typography>
        </Box>
      ) : !prep.isFileReady ? (
        <BuslogPreparationStepper
          isPreparing={prep.isPreparing}
          isPrepError={prep.isPrepError}
          prepError={prep.prepError}
          taskId={prep.taskId}
          statusData={prep.statusData}
          progress={prep.progress}
          activeStep={prep.activeStep}
          currentMessage={prep.currentMessage}
          steps={prep.steps}
          retry={prep.retry}
        />
      ) : (
        <Box className="charts-container">
          <Box className="charts-panels-area">
            <Box className="charts-top-row">
              <OverlayChart
                signals={signals.visualizedSignals}
                playbackTime={hasPlayback ? playback.currentTimeSec : null}
                onSeekToTime={hasPlayback ? playback.seekToTime : null}
                globalStartTs={prep.start_ts}
                globalEndTs={prep.end_ts}
              />
              <Box className="charts-playback-wrapper">
                <PlaybackControls
                  isPlaying={playback.isPlaying}
                  currentTimeSec={playback.currentTimeSec}
                  durationSec={playback.durationSec}
                  speed={playback.speed}
                  togglePlay={playback.togglePlay}
                  seekToTime={playback.seekToTime}
                  stepForward={playback.stepForward}
                  stepBackward={playback.stepBackward}
                  setSpeed={playback.setSpeed}
                  reset={playback.reset}
                  PLAYBACK_SPEEDS={playback.PLAYBACK_SPEEDS}
                  playbackTimestamp={playbackTimestamp}
                  hasSignals={signals.visualizedSignals.length > 0}
                />
              </Box>
            </Box>

            <Box className="charts-bottom-row">
              <TablePanel
                  currentTimeSec={playback.currentTimeSec}
                  visualizedSignals={signals.visualizedSignals}
                  globalStartTs={prep.start_ts}
                  playbackTimestamp={playbackTimestamp}
                  onRemoveSignal={signals.removeSignal}
                  prep={prep}
                  signals={signals}
                  filePath={filePath}
                  isSaved={isSaved}
                  handleSaveSearchClick={handleSaveSearchClick}
                  currentSignalsParam={currentSignalsParam}
              />
              <MapPanel
                  routePositions={signals.gpsMapData.routePositions}
                  gpsFrames={signals.gpsMapData.gpsFrames}
                  currentTimeSec={playback.currentTimeSec}
                  bounds={signals.gpsMapData.bounds}
                  isLoading={signals.isLoadingGps}
                  isError={!!signals.gpsError}
                  error={signals.gpsError}
                  onRefetch={signals.reloadGpsSignals}
                  globalStartTs={prep.start_ts}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BuslogPanels;
